import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const STAGES = [
  { id: 'identified', label: 'Identified', color: '#888' },
  { id: 'applied', label: 'Applied', color: '#2563eb' },
  { id: 'recruiter_screen', label: 'Recruiter Screen', color: '#7c3aed' },
  { id: 'interview', label: 'Interview', color: '#C9A84C' },
  { id: 'final_round', label: 'Final Round', color: '#f97316' },
  { id: 'offer', label: 'Offer 🎉', color: '#00712D' },
  { id: 'rejected', label: 'Closed', color: '#ccc' },
]

const REFERRAL_STATUSES = [
  { id: 'none', label: '⚠ No warm path yet' },
  { id: 'searching', label: '🔍 Searching for connection' },
  { id: 'found', label: '✓ Connection found' },
  { id: 'requested', label: '📨 Referral requested' },
  { id: 'confirmed', label: '🤝 Referral confirmed' },
]

export default function Pipeline({ session, onStatsUpdate, compact }) {
  const [items, setItems] = useState([])
  const [hotTen, setHotTen] = useState([])
  const [showAdd, setShowAdd] = useState(false)
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ company: '', role: '', source: 'LinkedIn', stage: 'identified', referral_status: 'none', notes: '', follow_up_date: '' })

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const [{ data }, { data: contacts }] = await Promise.all([
      supabase.from('pipeline').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false }),
      supabase.from('hot_ten').select('name, company').eq('user_id', session.user.id),
    ])
    setItems(data || [])
    setHotTen(contacts || [])
    setLoading(false)
  }

  async function add() {
    if (!form.company || !form.role) return
    const { data, error } = await supabase.from('pipeline').insert({ user_id: session.user.id, ...form, date_added: new Date().toISOString() }).select().single()
    if (!error && data) {
      await supabase.from('pipeline_stage_history').insert({ user_id: session.user.id, pipeline_id: data.id, stage: data.stage, entered_at: new Date().toISOString() })
    }
    setForm({ company: '', role: '', source: 'LinkedIn', stage: 'identified', referral_status: 'none', notes: '', follow_up_date: '' })
    setShowAdd(false)
    load()
    if (onStatsUpdate) onStatsUpdate()
  }

  async function updateStage(id, stage) {
    await supabase.from('pipeline').update({ stage }).eq('id', id)
    await supabase.from('pipeline_stage_history').insert({ user_id: session.user.id, pipeline_id: id, stage, entered_at: new Date().toISOString() })
    load()
    if (onStatsUpdate) onStatsUpdate()
  }

  async function updateReferral(id, referral_status) {
    await supabase.from('pipeline').update({ referral_status }).eq('id', id)
    load()
    if (onStatsUpdate) onStatsUpdate()
  }

  async function remove(id) {
    if (!window.confirm('Remove this opportunity?')) return
    await supabase.from('pipeline').delete().eq('id', id)
    load()
    if (onStatsUpdate) onStatsUpdate()
  }

  const active = items.filter(i => i.stage !== 'rejected')
  const closed = items.filter(i => i.stage === 'rejected')
  const getStage = id => STAGES.find(s => s.id === id) || STAGES[0]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 className="section-title" style={{ margin: 0 }}>Pipeline</h2>
          <p style={{ color: '#888', fontSize: '0.78rem', marginTop: '0.15rem' }}>{active.length} active · {closed.length} closed</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(!showAdd)} style={{ padding: '0.55rem 1rem', fontSize: '0.82rem' }}>+ Add role</button>
      </div>

      {showAdd && (
        <div className="card" style={{ marginBottom: '1rem', borderTop: '3px solid #00712D' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input className="input-field" placeholder="Company *" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} style={{ fontSize: '0.875rem' }} />
            <input className="input-field" placeholder="Role *" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ fontSize: '0.875rem' }} />
          </div>
          {form.company.trim().length > 1 && (() => {
            const match = hotTen.find(c => c.company && c.company.toLowerCase().includes(form.company.trim().toLowerCase()))
            return match ? (
              <div style={{ background: '#FFFBF0', border: '1px solid #C9A84C', borderRadius: '6px', padding: '0.6rem 0.75rem', marginBottom: '0.5rem', fontSize: '0.78rem', color: '#1A1A1A' }}>
                💡 You know <strong>{match.name}</strong> at {match.company}. Never apply naked — try for a referral before submitting.
              </div>
            ) : null
          })()}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <select className="input-field" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} style={{ fontSize: '0.875rem' }}>
              {['LinkedIn', 'Indeed', 'Company career page', 'Referral', 'Recruiter outreach', 'Networking', 'Other'].map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="input-field" value={form.referral_status} onChange={e => setForm({ ...form, referral_status: e.target.value })} style={{ fontSize: '0.875rem' }}>
              {REFERRAL_STATUSES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <textarea className="input-field" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ fontSize: '0.875rem', resize: 'none', marginBottom: '0.5rem' }} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" onClick={add}>Add to pipeline</button>
            <button className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <div style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>Loading...</div>}

      {!loading && items.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem', color: '#888' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🎯</div>
          <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>No opportunities yet</div>
          <p style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>Add roles you're interested in. Remember: work the warm path before applying.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem' }}>
        {active.map(item => {
          const stage = getStage(item.stage)
          const followUp = item.follow_up_date ? new Date(item.follow_up_date) : null
          const overdue = followUp && followUp < new Date()
          return (
            <div key={item.id} className="card" style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{item.company}</div>
                  <div style={{ color: '#555', fontSize: '0.78rem' }}>{item.role}</div>
                  <div style={{ color: '#aaa', fontSize: '0.72rem' }}>{item.source}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                  <span style={{ background: stage.color + '20', color: stage.color, padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700 }}>{stage.label}</span>
                  {overdue && <span className="badge" style={{ background: '#fee2e2', color: '#dc2626' }}>Follow up overdue</span>}
                </div>
              </div>
              {item.referral_status === 'none' && (
                <div style={{ fontSize: '0.72rem', color: '#C9A84C', fontWeight: 600, marginBottom: '0.5rem' }}>⚠ No warm path yet — spend 30 min finding a connection before applying</div>
              )}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <select value={item.stage} onChange={e => updateStage(item.id, e.target.value)} style={{ fontSize: '0.72rem', padding: '0.25rem 0.4rem', border: '1px solid #e8e8e8', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>
                  {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <select value={item.referral_status} onChange={e => updateReferral(item.id, e.target.value)} style={{ fontSize: '0.72rem', padding: '0.25rem 0.4rem', border: '1px solid #e8e8e8', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>
                  {REFERRAL_STATUSES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
                <button onClick={() => remove(item.id)} style={{ fontSize: '0.68rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
              </div>
              {item.notes && <div style={{ fontSize: '0.75rem', color: '#555', lineHeight: 1.5, marginTop: '0.5rem', borderTop: '1px solid #f0f0f0', paddingTop: '0.5rem' }}>{item.notes}</div>}
            </div>
          )
        })}
      </div>

      {closed.length > 0 && (
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Closed — {closed.length}</div>
          {closed.map(item => (
            <div key={item.id} style={{ padding: '0.6rem 0.75rem', border: '1px solid #f0f0f0', borderRadius: '7px', marginBottom: '0.35rem', opacity: 0.6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{item.company}</span>
                <span style={{ color: '#888', fontSize: '0.75rem' }}> · {item.role}</span>
              </div>
              <button onClick={() => remove(item.id)} style={{ fontSize: '0.72rem', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
